import { useState, useRef, useEffect } from 'react';
import { Box } from '@chakra-ui/react';

interface InlineEditProps {
  value: string;
  onSave: (value: string) => void;
  fontSize?: string;
  fontWeight?: string;
  color?: string;
  lineHeight?: string;
  multiline?: boolean;
  placeholder?: string;
  required?: boolean;
  borderColor?: string;
  textAlign?: 'left' | 'center' | 'right';
}

/** Derive a visible dashed-border color from the menu's text_color. */
export const deriveBorder = (textColor?: string): { idle: string; hover: string; active: string } => {
  if (!textColor) return { idle: 'rgba(59, 130, 246, 0.3)', hover: 'rgba(59, 130, 246, 0.5)', active: 'rgba(59, 130, 246, 0.5)' };
  // Parse hex to determine luminance
  const hex = textColor.replace('#', '');
  if (hex.length < 6) return { idle: 'rgba(59, 130, 246, 0.3)', hover: 'rgba(59, 130, 246, 0.5)', active: 'rgba(59, 130, 246, 0.5)' };
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const lum = (r * 299 + g * 587 + b * 114) / 1000;
  // Light text on dark bg → use light borders; dark text on light bg → use dark borders
  if (lum > 128) {
    return { idle: `rgba(${r}, ${g}, ${b}, 0.25)`, hover: `rgba(${r}, ${g}, ${b}, 0.45)`, active: `rgba(${r}, ${g}, ${b}, 0.45)` };
  }
  return { idle: `rgba(${r}, ${g}, ${b}, 0.2)`, hover: `rgba(${r}, ${g}, ${b}, 0.4)`, active: `rgba(${r}, ${g}, ${b}, 0.4)` };
};

const InlineEdit = ({ value, onSave, fontSize, fontWeight, color, lineHeight, multiline, placeholder, required = true, borderColor: borderColorProp, textAlign }: InlineEditProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  const border = deriveBorder(borderColorProp || color);

  useEffect(() => { setDraft(value); }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if ('select' in inputRef.current) inputRef.current.select();
    }
  }, [isEditing]);

  const save = () => {
    const trimmed = draft.trim();
    setIsEditing(false);
    if (trimmed !== value) {
      if (trimmed || !required) {
        onSave(trimmed);
      } else {
        setDraft(value);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      (inputRef.current as HTMLInputElement)?.blur();
    }
    if (e.key === 'Escape') {
      setDraft(value);
      setIsEditing(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    fontSize,
    fontWeight: fontWeight as never,
    color,
    lineHeight,
    background: 'transparent',
    border: `1px dashed ${border.active}`,
    borderRadius: '4px',
    outline: 'none',
    width: '100%',
    padding: '2px 4px',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    textAlign,
  };

  if (isEditing) {
    if (multiline) {
      return (
        <textarea
          ref={inputRef as React.RefObject<HTMLTextAreaElement>}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={save}
          onKeyDown={handleKeyDown}
          onClick={e => e.stopPropagation()}
          rows={3}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      );
    }
    return (
      <input
        ref={inputRef as React.RefObject<HTMLInputElement>}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={handleKeyDown}
        onClick={e => e.stopPropagation()}
        style={inputStyle}
      />
    );
  }

  return (
    <Box
      onClick={(e) => { e.stopPropagation(); setDraft(value); setIsEditing(true); }}
      cursor="text"
      borderWidth="1px"
      borderStyle="dashed"
      borderColor={border.idle}
      borderRadius="4px"
      px="4px"
      mx="-4px"
      transition="border-color 0.15s, background-color 0.15s"
      _hover={{ borderColor: border.hover, backgroundColor: `${border.hover.replace(/[\d.]+\)$/, '0.05)')}` }}
      fontSize={fontSize}
      fontWeight={fontWeight}
      color={color}
      lineHeight={lineHeight}
      textAlign={textAlign}
    >
      {value || <Box as="span" opacity={0.5}>{placeholder || '\u2014'}</Box>}
    </Box>
  );
};

export default InlineEdit;
